/**
 * Permit Operational Signals — Phase 2
 * ─────────────────────────────────────────────────────────────────────
 * Single canonical cross-domain interpretation layer for permit state.
 *
 * Reads canonical permitDetail + the Phase 1A–1F derivation helpers
 * (revisionRequest, inspectionLifecycle, permitCloseout, requirements
 * profile) and projects ONE deterministic shape consumed by Job Detail,
 * Install Readiness, Scheduling rows, Install Today, and Closeout.
 *
 * Invariants (LOCKED):
 *   1. Pure derivation. Never mutates job, never persists, never emits
 *      action-queue events, never schedules notifications.
 *   2. Reuses existing canonical helpers — does not duplicate readiness math.
 *   3. NO new truth: every signal is a re-projection of canonical fields.
 *   4. NO automation imports. Test enforces.
 *   5. NO financial data, NO PM attribution, NO customer-facing strings.
 */
import type { Job } from "@/data/types";
import { resolveRevisionReadiness, getRevisionRequest } from "./revisionRequest";
import {
  resolveInspectionReadiness,
  getInspectionLifecycle,
} from "./inspectionLifecycle";
import { resolveCloseoutReadiness, getPermitCloseout } from "./permitCloseout";
import { getRequirementProfile } from "./requirements";

export type PermitOperationalSeverity = "neutral" | "info" | "warn" | "blocker";

/** Status keys for Install Readiness consumers. */
export type PermitReadinessStatusKey =
  | "not_required"
  | "pending"
  | "permit_blocked"
  | "awaiting_review"
  | "inspection_correction_required"
  | "permit_ready";

/** Status keys for Scheduling row chips. */
export type PermitSchedulingStatusKey =
  | "not_required"
  | "permit_blocked"
  | "municipality_review"
  | "failed_inspection"
  | "permit_cleared"
  | "pending";

/** Status keys for Closeout consumers. */
export type PermitCloseoutStatusKey =
  | "not_required"
  | "no_signal"
  | "checklist_in_progress"
  | "ready_for_closure_confirmation"
  | "municipality_closure_confirmed";

export interface PermitOperationalSignals {
  /** True only when permit is required AND something must be resolved before install. */
  installBlocker: boolean;
  /** Reason text for install blocker (empty when not a blocker). */
  installBlockerReason: string;

  schedulingStatus: PermitSchedulingStatusKey;
  schedulingLabel: string;

  readinessStatus: PermitReadinessStatusKey;
  readinessLabel: string;

  closeoutStatus: PermitCloseoutStatusKey;
  closeoutLabel: string;

  /** Highest-severity tone across the four lenses. */
  operationalSeverity: PermitOperationalSeverity;
  /** One-line operator summary for strips/chips. */
  summaryLabel: string;

  /** Sub-signal echoes for the strip UI (read-only). */
  permitStageLabel: string;
  inspectionLabel?: string;
  revisionLabel?: string;
  closeoutSubLabel?: string;
  municipalityProfileLabel: string;
  usesGenericProfile: boolean;
}

const SEVERITY_RANK: Record<PermitOperationalSeverity, number> = {
  neutral: 0,
  info: 1,
  warn: 2,
  blocker: 3,
};

function maxSeverity(
  a: PermitOperationalSeverity,
  b: PermitOperationalSeverity,
): PermitOperationalSeverity {
  return SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;
}

/**
 * Pure derivation. Single canonical reading of permit health for ALL
 * downstream consumers — guarantees consistent interpretation.
 */
export function resolvePermitOperationalSignals(
  job: Job | null | undefined,
): PermitOperationalSignals {
  const profile = getRequirementProfile(job?.permitDetail?.municipality);
  const profileLabel = profile.label;
  const usesGeneric = profile.municipalityId === "generic";

  // Permit not required → operations can proceed unconstrained.
  if (!job?.permitRequired) {
    return {
      installBlocker: false,
      installBlockerReason: "",
      schedulingStatus: "not_required",
      schedulingLabel: "Permit not required",
      readinessStatus: "not_required",
      readinessLabel: "Permit not required",
      closeoutStatus: "not_required",
      closeoutLabel: "Permit not required",
      operationalSeverity: "neutral",
      summaryLabel: "Permit not required",
      permitStageLabel: "Not Required",
      municipalityProfileLabel: profileLabel,
      usesGenericProfile: usesGeneric,
    };
  }

  const pd = job.permitDetail;
  const stage = pd?.detailedStage ?? "Permit Required";

  const revisionReq = getRevisionRequest(job);
  const revision = resolveRevisionReadiness(revisionReq);
  const inspectionLifecycle = getInspectionLifecycle(job);
  const inspection = resolveInspectionReadiness(inspectionLifecycle);
  const closeout = resolveCloseoutReadiness(getPermitCloseout(job));

  // ── Revision (highest priority pre-approval blocker) ─────────────
  const revisionUnresolved = !!revisionReq && revision.unresolvedCount > 0;
  const revisionLabel = revisionReq
    ? `Revision Requested · ${revision.progressLabel}`
    : undefined;

  // ── Inspection failure (highest priority post-approval blocker) ──
  const failedInspections = inspection.unresolvedFailures;
  const hasInspectionFailure = failedInspections.length > 0;
  const inspectionLabel =
    inspection.status === "not_started"
      ? undefined
      : hasInspectionFailure
        ? `Inspection Failure · ${failedInspections[0].inspectionType} failed`
        : inspection.status === "complete"
          ? "Inspections complete"
          : `Inspections Active · ${inspection.progressLabel}`;

  // ── Closeout sub-label ───────────────────────────────────────────
  const hasCloseout = closeout.state !== "not_started";
  const municipalityClosureConfirmed =
    !!getPermitCloseout(job)?.closureConfirmedDate || stage === "Closed";
  const closeoutSubLabel = !hasCloseout
    ? undefined
    : closeout.state === "ready"
      ? "Permit Closeout Ready"
      : `Closeout Pending · ${closeout.progressLabel}`;

  // ── Determine readiness lens (Install Readiness consumer) ────────
  let readinessStatus: PermitReadinessStatusKey;
  let readinessLabel: string;
  if (revisionUnresolved) {
    readinessStatus = "permit_blocked";
    readinessLabel = "Permit Blocked · Revision Requested";
  } else if (hasInspectionFailure) {
    readinessStatus = "inspection_correction_required";
    readinessLabel = "Inspection Correction Required";
  } else if (stage === "Approved" || stage === "Closed") {
    readinessStatus = "permit_ready";
    readinessLabel = "Permit Ready";
  } else if (stage === "Submitted" || stage === "Under Review" || stage === "Resubmitted") {
    readinessStatus = "awaiting_review";
    readinessLabel = "Awaiting Municipality Review";
  } else {
    readinessStatus = "pending";
    readinessLabel = "Permit Pending";
  }

  // ── Scheduling lens ──────────────────────────────────────────────
  let schedulingStatus: PermitSchedulingStatusKey;
  let schedulingLabel: string;
  if (revisionUnresolved) {
    schedulingStatus = "permit_blocked";
    schedulingLabel = "Permit Blocked";
  } else if (hasInspectionFailure) {
    schedulingStatus = "failed_inspection";
    schedulingLabel = "Failed Inspection";
  } else if (stage === "Submitted" || stage === "Under Review" || stage === "Resubmitted") {
    schedulingStatus = "municipality_review";
    schedulingLabel = "Municipality Review";
  } else if (stage === "Approved" || stage === "Closed") {
    schedulingStatus = "permit_cleared";
    schedulingLabel = "Permit Cleared";
  } else {
    schedulingStatus = "pending";
    schedulingLabel = "Permit Pending";
  }

  // ── Closeout lens ────────────────────────────────────────────────
  let closeoutStatus: PermitCloseoutStatusKey;
  let closeoutLabel: string;
  if (municipalityClosureConfirmed) {
    closeoutStatus = "municipality_closure_confirmed";
    closeoutLabel = "Municipality closure confirmed";
  } else if (closeout.state === "ready") {
    closeoutStatus = "ready_for_closure_confirmation";
    closeoutLabel = "Permit ready for closure confirmation";
  } else if (closeout.state === "not_ready") {
    closeoutStatus = "checklist_in_progress";
    closeoutLabel = "Permit closure checklist in progress";
  } else {
    closeoutStatus = "no_signal";
    closeoutLabel = "Closeout not started";
  }

  // ── Install blocker (single source of truth for ops blockers) ────
  const installBlocker = revisionUnresolved || hasInspectionFailure;
  const installBlockerReason = revisionUnresolved
    ? `Permit revision requested · ${revision.progressLabel}`
    : hasInspectionFailure
      ? `Inspection correction required · ${failedInspections[0].inspectionType}`
      : "";

  // ── Severity rollup ──────────────────────────────────────────────
  let severity: PermitOperationalSeverity = "neutral";
  if (revisionUnresolved || hasInspectionFailure) severity = "blocker";
  else if (
    schedulingStatus === "municipality_review" ||
    readinessStatus === "pending" ||
    (closeout.state === "not_ready" && stage !== "Closed")
  ) severity = maxSeverity(severity, "warn");
  else if (
    schedulingStatus === "permit_cleared" ||
    closeoutStatus === "municipality_closure_confirmed"
  ) severity = maxSeverity(severity, "info");

  // ── Summary label ────────────────────────────────────────────────
  const summaryLabel = revisionUnresolved
    ? `Revision Requested · ${revision.progressLabel}`
    : hasInspectionFailure
      ? `Inspection Failure · ${failedInspections[0].inspectionType} failed`
      : municipalityClosureConfirmed
        ? "Permit Closed · municipality confirmed"
        : closeout.state === "ready"
          ? "Permit Closeout Ready"
          : inspection.status === "active"
            ? `Inspections Active · ${inspection.progressLabel}`
            : inspection.status === "complete"
              ? "Inspections complete"
              : stage === "Approved"
                ? "Permit Approved"
                : schedulingStatus === "municipality_review"
                  ? "Awaiting Municipality Review"
                  : `Permit ${stage}`;

  return {
    installBlocker,
    installBlockerReason,
    schedulingStatus,
    schedulingLabel,
    readinessStatus,
    readinessLabel,
    closeoutStatus,
    closeoutLabel,
    operationalSeverity: severity,
    summaryLabel,
    permitStageLabel: stage,
    inspectionLabel,
    revisionLabel,
    closeoutSubLabel,
    municipalityProfileLabel: profileLabel,
    usesGenericProfile: usesGeneric,
  };
}
